'use client'

import { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import styles from './index.module.css'
import petalImage from './assets/petal.png'
import arrowImage from './assets/arrow.png'

interface Petal {
  id: number
  left: string
  delay: string
  duration: string
  opacity: number
  size: number
  swayAmount: number
  swaySpeed: number
  rotateAmount: number
}

type ArrowDirection = 'leftToRight' | 'rightToLeft' | 'diagonalUp' | 'diagonalDown'

interface Arrow {
  id: number
  direction: ArrowDirection
  top?: string
  delay: string
  duration: string
  size: number
}

interface ValentineEffectProps {
  isActive: boolean
  durationMs?: number
  petalIntensity?: number
  arrowIntensity?: number
  gradualStart?: boolean
}

const generatePetal = (id: number, initial = false, gradualStart = false): Petal => {
  const duration = 3 + Math.random() * 4
  let delay = '0s'

  if (initial) {
    delay = gradualStart ? `${Math.random() * 2}s` : `${-Math.random() * duration}s`
  }

  return {
    id,
    left: `${Math.random() * 100}%`,
    delay,
    duration: `${duration}s`,
    opacity: Math.random() * 0.4 + 0.6,
    size: 30 + Math.random() * 40,
    swayAmount: 3 + Math.random() * 15,
    swaySpeed: 2 + Math.random() * 3,
    rotateAmount: 5 + Math.random() * 25,
  }
}

const arrowDirections: ArrowDirection[] = ['leftToRight', 'rightToLeft', 'diagonalUp', 'diagonalDown']

const generateArrow = (id: number, initial = false): Arrow => {
  const duration = 3 + Math.random() * 2
  const direction = arrowDirections[Math.floor(Math.random() * arrowDirections.length)]
  let delay = '0s'

  if (initial) {
    delay = `${Math.random() * 3}s`
  }

  // For horizontal arrows, randomize vertical position
  let top: string | undefined
  if (direction === 'leftToRight' || direction === 'rightToLeft') {
    top = `${10 + Math.random() * 70}%`
  }

  return {
    id,
    direction,
    top,
    delay,
    duration: `${duration}s`,
    size: 100 + Math.random() * 100,
  }
}

const getArrowClassName = (direction: ArrowDirection): string => {
  switch (direction) {
    case 'leftToRight':
      return styles.arrowLeftToRight
    case 'rightToLeft':
      return styles.arrowRightToLeft
    case 'diagonalUp':
      return styles.arrowDiagonalUp
    case 'diagonalDown':
      return styles.arrowDiagonalDown
  }
}

export function ValentineEffect({ isActive, durationMs, petalIntensity = 1, arrowIntensity = 1, gradualStart = false }: ValentineEffectProps) {
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [petals, setPetals] = useState<Petal[]>([])
  const [arrows, setArrows] = useState<Arrow[]>([])
  const prevIsActiveRef = useRef(isActive)

  // Handle fade out when isActive transitions from true to false
  useEffect(() => {
    const wasActive = prevIsActiveRef.current
    prevIsActiveRef.current = isActive

    if (wasActive && !isActive) {
      // Start fade out when isActive becomes false (use setTimeout to avoid synchronous setState in effect)
      const fadeStartTimer = setTimeout(() => {
        setIsFadingOut(true)
      }, 0)

      // Wait for fade transition to complete before hiding
      const fadeTimer = setTimeout(() => {
        setIsVisible(false)
        setIsFadingOut(false)
        setPetals([])
        setArrows([])
      }, 1000) // Match the CSS transition duration

      return () => {
        clearTimeout(fadeStartTimer)
        clearTimeout(fadeTimer)
      }
    }

    if (!isActive) {
      return
    }

    // Show and reset fade state when becoming active (use setTimeout to avoid synchronous setState in effect)
    const activateTimer = setTimeout(() => {
      setIsVisible(true)
      setIsFadingOut(false)

      // Generate initial petals
      const initialPetals: Petal[] = []
      const petalCount = Math.floor(30 * petalIntensity)
      for (let i = 0; i < petalCount; i++) {
        initialPetals.push(generatePetal(i, true, gradualStart))
      }
      setPetals(initialPetals)

      // Generate initial arrows
      const initialArrows: Arrow[] = []
      const arrowCount = Math.floor(5 * arrowIntensity)
      for (let i = 0; i < arrowCount; i++) {
        initialArrows.push(generateArrow(i, true))
      }
      setArrows(initialArrows)
    }, 0)

    // If no duration is specified, don't auto fade out
    if (durationMs === undefined) {
      return () => clearTimeout(activateTimer)
    }

    // Start fading out 1s before duration ends
    const fadeOutTime = Math.max(0, durationMs - 1000)
    const timer = setTimeout(() => {
      setIsFadingOut(true)
    }, fadeOutTime)

    return () => {
      clearTimeout(activateTimer)
      clearTimeout(timer)
    }
  }, [isActive, durationMs, petalIntensity, arrowIntensity, gradualStart])

  if (!isVisible) return null

  return (
    <div
      className={clsx(styles.valentineContainer, {
        [styles.fadeOut]: isFadingOut,
      })}
    >
      {/* Falling Petals */}
      {petals.map((petal) => (
        <div
          key={`petal-${petal.id}`}
          className={styles.petal}
          style={
            {
              left: petal.left,
              animationDelay: petal.delay,
              animationDuration: petal.duration,
              opacity: petal.opacity,
              width: `${petal.size}px`,
              height: `${petal.size}px`,
              '--sway-amount': `${petal.swayAmount}px`,
              '--sway-speed': `${petal.swaySpeed}s`,
              '--rotate-amount': `${petal.rotateAmount}deg`,
            } as React.CSSProperties
          }
          onAnimationEnd={() => {
            if (!isFadingOut && isActive) {
              setPetals((prev) => prev.map((p) => (p.id === petal.id ? generatePetal(Date.now() + Math.random()) : p)))
            }
          }}
        >
          <Image src={petalImage} alt="" width={petal.size} height={petal.size} className={styles.petalImage} />
        </div>
      ))}

      {/* Flying Arrows */}
      {arrows.map((arrow) => (
        <div
          key={`arrow-${arrow.id}`}
          className={clsx(styles.arrow, getArrowClassName(arrow.direction))}
          style={{
            top: arrow.top,
            animationDelay: arrow.delay,
            animationDuration: arrow.duration,
            width: `${arrow.size}px`,
          }}
          onAnimationEnd={() => {
            if (!isFadingOut && isActive) {
              setArrows((prev) => prev.map((a) => (a.id === arrow.id ? generateArrow(Date.now() + Math.random()) : a)))
            }
          }}
        >
          <Image src={arrowImage} alt="" width={arrow.size} height={Math.round(arrow.size * 0.15)} className={styles.arrowImage} />
        </div>
      ))}
    </div>
  )
}
